import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import api from '../services/api';
import useWorkspaceBasePath from '../hooks/useWorkspaceBasePath';
import { Lock, Package, Lightbulb, Sparkles, Rocket, User, Briefcase, BarChart2, Truck, Factory, ClipboardList, Store, Users, Megaphone, FileText, RotateCw, Banknote, CreditCard, Settings, Target, TrendingUp, Building2, WifiOff, HandCoins, LogOut, Smartphone } from 'lucide-react';

// Composant de restriction d'accès réutilisable
// eslint-disable-next-line react/prop-types
const AccessRestrictionPage = ({ moduleCode, moduleName, moduleIcon, reason, userSubscription, onBack }) => {
  const [availablePlans, setAvailablePlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);

  const loadAvailablePlans = useCallback(async () => {
    try {
      const plansRes = await api.get('/subscriptions/plans');
      const plansWithModule = plansRes.data
        .filter(plan => plan.modules && plan.modules.includes(moduleCode))
        .sort((a, b) => (a.display_order || 0) - (b.display_order || 0) || (a.prix_mensuel - b.prix_mensuel));
      setAvailablePlans(plansWithModule);
    } catch (err) {
      console.error('Erreur chargement packs:', err);
    } finally {
      setLoadingPlans(false);
    }
  }, [moduleCode]);

  useEffect(() => {
    loadAvailablePlans();
  }, [loadAvailablePlans]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-orange-500 to-red-500 p-8 text-center text-white relative overflow-hidden">
            <div className="absolute inset-0 bg-black/10"></div>
            <div className="relative">
              <span className="mb-4 block animate-bounce"><Lock size={64} /></span>
              <h1 className="text-3xl md:text-4xl font-bold mb-3">Accès Restreint</h1>
              <p className="text-orange-100 text-lg">
                Cette fonctionnalité nécessite un abonnement adapté
              </p>
            </div>
          </div>
          
          {/* Contenu */}
          <div className="p-6 md:p-8">
            {/* Module requis */}
            <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-2xl p-5 mb-6 border-2 border-purple-100">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-md">
                  <span>{moduleIcon || <Package size={32} />}</span>
                </div>
                <div className="flex-1">
                  <div className="text-xs text-purple-600 font-semibold uppercase tracking-wide mb-1">Module requis</div>
                  <div className="text-xl font-bold text-gray-900">{moduleName}</div>
                </div>
              </div>
            </div>
            
            {/* Message personnalisé */}
            <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-5 mb-6">
              <div className="flex items-start gap-3">
                <Lightbulb size={24} />
                <div>
                  <div className="font-semibold text-blue-800 mb-1">
                    {userSubscription ? "Upgrade nécessaire" : "Aucun abonnement actif"}
                  </div>
                  <p className="text-blue-700 text-sm">{reason}</p>
                </div>
              </div>
            </div>
            
            {/* Packs disponibles */}
            {loadingPlans ? (
              <div className="flex justify-center py-4">
                <div className="animate-spin rounded-full h-8 w-8 border-4 border-purple-500 border-t-transparent"></div>
              </div>
            ) : availablePlans.length > 0 ? (
              <>
                <div className="text-center mb-4">
                  <span className="text-sm font-semibold text-gray-600 bg-gray-100 px-4 py-2 rounded-full">
                    <Sparkles size={16} className="inline mr-1" /> {availablePlans.length === 1 
                      ? `1 pack donne accès à "${moduleName}"` 
                      : `${availablePlans.length} packs donnent accès à "${moduleName}"`}
                  </span>
                </div>
                
                <div className="grid gap-3">
                  {availablePlans.map((plan, index) => (
                    <div 
                      key={plan.id}
                      className={`border-2 rounded-2xl p-4 flex items-center justify-between hover:shadow-lg transition-all cursor-pointer ${
                        index === 0 
                          ? 'border-purple-300 bg-purple-50/50 ring-2 ring-purple-200' 
                          : 'border-gray-100 hover:border-purple-300'
                      }`}
                      onClick={() => window.location.href = '/my-subscription'}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: plan.couleur + '20' }}>
                          <span className="text-2xl">{plan.icone}</span>
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-gray-900">{plan.nom}</span>
                            {index === 0 && (
                              <span className="text-xs px-2 py-0.5 bg-purple-600 text-white rounded-full">Recommandé</span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {plan.module_names?.length || 0} module{(plan.module_names?.length || 0) > 1 ? 's' : ''} inclus
                          </div>
                          <div className="text-xs text-gray-400 mt-0.5">
                            {plan.module_names?.slice(0, 4).join(' • ')}
                            {plan.module_names?.length > 4 && ` +${plan.module_names.length - 4}`}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold" style={{ color: plan.couleur }}>
                          {Number(plan.prix_mensuel).toLocaleString()}
                        </div>
                        <div className="text-xs text-gray-400">XOF/mois</div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="bg-gray-50 border-2 border-gray-200 rounded-2xl p-5 mb-6 text-center">
                <span className="mb-3 block"><Lock size={48} /></span>
                <div className="font-semibold text-gray-700">
                  Le module "{moduleName}" n'est disponible dans aucun pack actuellement
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  Contactez l'administrateur pour plus d'informations.
                </p>
              </div>
            )}
            
            {/* Bouton principal */}
            <div className="mt-8">
              <Link
                to="/my-subscription"
                className="w-full flex items-center justify-center gap-3 px-8 py-4 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-2xl font-bold text-lg hover:from-purple-700 hover:to-indigo-700 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                <Rocket size={18} className="inline" />
                <span>Voir les packs d'abonnement</span>
              </Link>
            </div>
            
            {/* Bouton retour */}
            {onBack && (
              <div className="mt-4 text-center">
                <button
                  onClick={onBack}
                  className="text-gray-400 hover:text-gray-600 transition-colors text-sm"
                >
                  ← Retourner à la page précédente
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const BusinessWorkspace = () => {
  const { user, logout } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const basePath = useWorkspaceBasePath();
  const isPortal = location.pathname.startsWith('/portal/');

  const handleLogout = () => {
    logout();
    navigate(isPortal ? '/portal/workspace/login' : '/login');
  };
  const [access, setAccess] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Fermer le sidebar quand on change de page (mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const checkAccess = useCallback(async () => {
    try {
      const res = await api.get('/business/access');
      setAccess(res.data);
      
      if (res.data.can_access) {
        loadDashboard();
      }
    } catch (err) {
      console.error(err);
      setAccess({ can_access: false });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAccess();
  }, [checkAccess]);

  const loadDashboard = async () => {
    try {
      const res = await api.get('/business/dashboard');
      setStats(res.data.stats);
    } catch (err) {
      console.error(err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  // Accès refusé - cas non auteur (message spécial)
  if (!access?.is_author) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 flex items-center justify-center p-4">
        <div className="max-w-lg w-full bg-white rounded-3xl shadow-2xl overflow-hidden">
          <div className="bg-gradient-to-r from-indigo-500 to-purple-500 p-8 text-center text-white">
            <span className="mb-4 block"><User size={48} /></span>
            <h1 className="text-2xl font-bold mb-2">Devenez Auteur</h1>
            <p className="text-indigo-100">Pour accéder à l'espace de travail</p>
          </div>
          <div className="p-6">
            <p className="text-gray-600 mb-6 text-center">
              L'espace de travail entreprise est réservé aux <strong>auteurs</strong>. 
              Activez le mode auteur dans votre profil pour commencer.
            </p>
            <Link
              to="/profile"
              className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold rounded-2xl hover:from-indigo-700 hover:to-purple-700 transition-all shadow-lg"
            >
              <Sparkles size={18} className="inline" />
              <span>Devenir Auteur</span>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Accès refusé - pas le module workspace
  if (!access?.can_access) {
    const reason = access?.subscription?.plan_code 
      ? `Votre pack actuel (${access.subscription.plan_nom}) ne donne pas accès à l'espace de travail. Passez à un pack supérieur pour en profiter !`
      : "Souscrivez à un abonnement pour débloquer l'espace de travail et gérer votre activité professionnelle.";
    
    return (
      <AccessRestrictionPage
        moduleCode="workspace"
        moduleName="Espace de Travail"
        moduleIcon={<Briefcase size={32} />}
        reason={reason}
        userSubscription={access?.subscription}
        onBack={() => window.history.back()}
      />
    );
  }

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  // Menu items avec vérification du module shop
  const hasShopAccess = access?.has_shop_module;
  
  const homeLink = isPortal ? basePath : '/';

  const menuSections = [
    {
      items: [
        { path: basePath, icon: <BarChart2 size={18} />, label: 'Tableau de bord', exact: true },
      ]
    },
    {
      title: 'Stock & Produits',
      items: [
        { path: `${basePath}/products`, icon: <Package size={18} />, label: 'Produits & Stock' },
        { path: `${basePath}/restock`, icon: <Truck size={18} />, label: 'Ravitaillement' },
        { path: `${basePath}/suppliers`, icon: <Factory size={18} />, label: 'Fournisseurs' },
        { path: `${basePath}/inventories`, icon: <ClipboardList size={18} />, label: 'Inventaires' },
      ]
    },
    {
      title: 'Ventes & Clients',
      items: [
        { path: `${basePath}/shop`, icon: <Store size={18} />, label: 'Boutique en ligne', requiresShop: true },
        { path: `${basePath}/clients`, icon: <Users size={18} />, label: 'Clients' },
        { path: `${basePath}/marketing`, icon: <Megaphone size={18} />, label: 'Marketing' },
      ]
    },
    {
      title: 'Facturation',
      items: [
        { path: `${basePath}/invoices`, icon: <FileText size={18} />, label: 'Factures' },
        { path: `${basePath}/quotes`, icon: <ClipboardList size={18} />, label: 'Devis' },
        { path: `${basePath}/returns`, icon: <RotateCw size={18} />, label: 'Avoirs / Retours' },
      ]
    },
    {
      title: 'Finances',
      items: [
        { path: `${basePath}/payments`, icon: <Banknote size={18} />, label: 'Paiements' },
        { path: `${basePath}/expenses`, icon: <Banknote size={18} />, label: 'Dépenses' },
        { path: `${basePath}/trusts`, icon: <HandCoins size={18} />, label: 'Dépôts & Prêts' },
        { path: `${basePath}/wallet`, icon: <CreditCard size={18} />, label: 'Portefeuille', disabled: true, disabledReason: 'Bientôt disponible' },
      ]
    },
    {
      title: 'Services',
      items: [
        { path: `${basePath}/prestations`, icon: <Settings size={18} />, label: 'Prestations' },
        { path: '/pro/service-requests', icon: <Target size={18} />, label: 'Revenus Services', external: true },
      ]
    },
    {
      title: 'Mobile Money',
      items: [
        { path: `${basePath}/mobile-money`, icon: <Smartphone size={18} />, label: 'Point de Transfert' },
      ]
    },
    {
      title: 'Analyse & Rapports',
      items: [
        { path: `${basePath}/report`, icon: <FileText size={18} />, label: 'Bilan' },
        { path: `${basePath}/sales-analytics`, icon: <TrendingUp size={18} />, label: 'Analyse Ventes' },
      ]
    },
    {
      title: 'Organisation',
      items: [
        { path: `${basePath}/team`, icon: <Users size={18} />, label: 'Mon Équipe' },
        { path: `${basePath}/team-invitations`, icon: <Building2 size={18} />, label: 'Workspaces partagés' },
        { path: `${basePath}/settings`, icon: <Settings size={18} />, label: 'Paramètres' },
        { path: `${basePath}/offline`, icon: <WifiOff size={18} />, label: 'Mode Hors Ligne' },
      ]
    },
  ];

  return (
    <div className="min-h-screen bg-gray-100 lg:flex">
      {/* Mobile Header */}
      <div className={`lg:hidden fixed top-0 left-0 right-0 shadow-md z-50 px-4 py-3 flex items-center justify-between ${isPortal ? 'bg-gradient-to-r from-teal-600 to-emerald-600' : 'bg-white'}`}>
        <button
          onClick={() => setSidebarOpen(true)}
          className={`p-2 rounded-lg ${isPortal ? 'hover:bg-white/20 text-white' : 'hover:bg-gray-100 text-gray-600'}`}
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
        <h2 className={`font-bold ${isPortal ? 'text-white' : 'text-gray-800'}`}>
          {isPortal ? 'DJAMANN Workspace' : 'Mon Espace'}
        </h2>
        <Link
          to={homeLink}
          className={`p-2 rounded-lg ${isPortal ? 'hover:bg-white/20 text-white' : 'hover:bg-gray-100 text-gray-600'}`}
          title={isPortal ? 'Tableau de bord' : 'Pour toi'}
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
          </svg>
        </Link>
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-30"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 bottom-0 left-0 z-40 bg-white shadow-lg transition-all duration-300 flex flex-col
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        w-64 lg:w-64 lg:top-16
      `}>
        <div className={`p-4 border-b flex items-center justify-between flex-shrink-0 ${isPortal ? 'bg-gradient-to-r from-teal-600 to-emerald-600' : ''}`}>
          <div>
            <h2 className={`font-bold ${isPortal ? 'text-white' : 'text-gray-800'}`}>
              {isPortal ? 'DJAMANN Workspace' : 'Mon Espace'}
            </h2>
            <p className={`text-xs ${isPortal ? 'text-teal-200' : 'text-gray-500'}`}>
              {isPortal ? 'Votre espace professionnel' : 'Gestion entreprise'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to={homeLink}
              className={`p-2 rounded-lg transition-colors ${isPortal ? 'hover:bg-white/20 text-white' : 'hover:bg-teal-100 text-teal-600'}`}
              title={isPortal ? 'Tableau de bord' : 'Pour toi'}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
            </Link>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-2 hover:bg-gray-100 rounded-lg text-gray-500"
            >
              ✕
            </button>
          </div>
        </div>
        
        <nav className="p-3 flex-1 min-h-0 overflow-y-auto">
          {menuSections.map((section, sIdx) => (
            <div key={sIdx} className={sIdx > 0 ? 'mt-3 pt-3 border-t border-gray-100' : ''}>
              {section.title && (
                <div className="px-3 mb-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
                  {section.title}
                </div>
              )}
              <div className="space-y-0.5">
                {section.items.map(item => {
                  const isLocked = item.requiresShop && !hasShopAccess;
                  const isDisabled = item.disabled;
                  
                  if (isLocked || isDisabled) {
                    return (
                      <div
                        key={item.path}
                        className="flex items-center gap-3 px-3 py-2 rounded-xl text-gray-400 cursor-not-allowed relative group"
                      >
                        <span className="text-lg opacity-50">{item.icon}</span>
                        <span className="text-sm opacity-50">{item.label}</span>
                        <Lock size={14} className="ml-auto" />
                        <div className="absolute left-full ml-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50">
                          {item.disabledReason || 'Requiert l\'abonnement Professionnel'}
                        </div>
                      </div>
                    );
                  }
                  
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setSidebarOpen(false)}
                      className={`flex items-center gap-3 px-3 py-2 rounded-xl transition ${
                        (item.exact ? location.pathname === item.path : isActive(item.path))
                          ? 'bg-teal-50 text-teal-600 font-medium'
                          : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <span className="text-lg">{item.icon}</span>
                      <span className="text-sm">{item.label}</span>
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        {/* Portal: user info + logout */}
        {isPortal && user && (
          <div className="p-4 border-t bg-gray-50 flex-shrink-0">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-xl flex items-center justify-center shadow-inner">
                <span className="text-white font-bold text-sm">{user.nom?.[0]?.toUpperCase() || 'U'}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-800 text-sm truncate">{user.nom}</p>
                <p className="text-xs text-gray-500 truncate">{user.email}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-red-50 text-red-600 rounded-xl hover:bg-red-100 transition-colors text-sm font-medium"
            >
              <LogOut size={16} />
              Déconnexion
            </button>
          </div>
        )}
      </aside>

      {/* Main content */}
      <main className="flex-1 lg:ml-64 pt-16 lg:pt-0 transition-all duration-300 min-h-screen">
        <Outlet context={{ stats, refreshStats: loadDashboard }} />
      </main>
    </div>
  );
};

export default BusinessWorkspace;
